<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import {
    buildRoofMapMarkerCustomHtml,
    ROOF_MAP_MARKER_PX,
} from "./pv-roof-map.util"
import type { CadastreMapFeature } from "@modules/pv/utils/buildingCadastreMapGeojson"

// ================================
// TYPES
// ================================
type MapTileLayerType = "streets" | "satellite"

type PVRoofMapSegment = {
    title: string
    content: { label: string; value: string }[]
    /**
     * Numéro sur la pastille (1-based) — en général = index d’affichage du pan.
     */
    index: number
    /** [latitude, longitude] */
    coordinates: [number, number]
    /** true = icône bleue (case cochée / pan actif), false = gris. */
    isActive: boolean
}

// ================================
// PROPS
// ================================
const props = withDefaults(
    defineProps<{
        /** [latitude, longitude] — centrage carte */
        addressLocation?: [number, number] | null
        segments?: PVRoofMapSegment[] | null
        /** Parcelle cadastrale (remplissage vert via `BaseMap` / `parcelStyle: "selected"`). */
        cadastreFeature?: CadastreMapFeature | null
        /** Hauteur CSS de la carte (ex. `23rem`, `31.25rem`). */
        mapHeight?: string
        /** Classes Tailwind du conteneur autour de la carte. */
        mapContainerClass?: string
        /** Fond initial : plan ou satellite (voir `BaseMap`). */
        defaultTileLayerType?: MapTileLayerType
    }>(),
    {
        addressLocation: null,
        segments: null,
        cadastreFeature: null,
        mapHeight: "31.25rem",
        mapContainerClass:
            "w-full max-w-6xl overflow-hidden rounded-lg shadow-sm",
        defaultTileLayerType: "streets",
    }
)

// ================================
// COMPUTED
// ================================
const getGeoJson = computed(() => {
    const features: Array<
        | CadastreMapFeature
        | {
              type: "Feature"
              geometry: { type: "Point"; coordinates: [number, number] }
              properties: Record<string, unknown>
          }
    > = []

    if (props.cadastreFeature) {
        features.push(props.cadastreFeature)
    }

    const segmentFeatures =
        props.segments?.map((item) => {
            const [lat, lng] = item.coordinates
            const labelLines = item.content
                .map((c) => `${c.label} : ${c.value}`)
                .join(" · ")
            const name = `${item.title}${labelLines ? ` — ${labelLines}` : ""}`

            return {
                type: "Feature" as const,
                geometry: {
                    type: "Point" as const,
                    coordinates: [lng, lat] as [number, number],
                },
                properties: {
                    name,
                    title: item.title,
                    customHtml: buildRoofMapMarkerCustomHtml(
                        item.index,
                        item.isActive
                    ),
                    iconSize: [ROOF_MAP_MARKER_PX, ROOF_MAP_MARKER_PX] as [
                        number,
                        number,
                    ],
                },
            }
        }) ?? []

    features.push(...segmentFeatures)

    return {
        type: "FeatureCollection" as const,
        features,
    }
})
</script>

<template>
    <div
        v-if="addressLocation && addressLocation[0] != null"
        :class="mapContainerClass"
    >
        <BaseMap
            id="pv-roof-map"
            class="h-full w-full"
            :center="addressLocation"
            :zoom="20"
            :min-zoom="20"
            :max-zoom="20"
            :geojson="getGeoJson"
            :clustering="false"
            :auto-fit-bounds="false"
            :height="mapHeight"
            :default-tile-layer-type="defaultTileLayerType"
        />
    </div>
</template>

<style scoped>
/* Marqueur (divIcon) : forcer le rendu, éviter un max-width:100% global sur les images */
:deep(.pv-roof-map__marker) {
    background: transparent;
    border: none;
}
:deep(.pv-roof-map__marker img) {
    max-width: none;
    min-width: 0;
}
</style>
